import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  List,
  Avatar,
  Tooltip,
  Spin,
  Button,
  Empty,
  Table,
  Tabs,
  Input,
  Dropdown,
  Menu,
  Tag,
  message,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
  LinkOutlined,
  SearchOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import DatePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import { channelsApi } from "./api/channelsApi";

const { TabPane } = Tabs;
const { Title, Text } = Typography;

const AUTO_REFRESH_MS = 45000;

const STYLES = {
  cardShadow: "0 10px 32px rgba(15,23,42,0.06)",
  cardBorderRadius: "18px",
  textMain: "#0F172A",
  textLight: "#64748B",
  success: "#10B981",
  danger: "#EF4444",
  warning: "#F59E0B",
  primary: "#2563EB",
};

const safeLower = (v) => String(v || "").trim().toLowerCase();

const getLocalCurrency = () =>
  String(
    localStorage.getItem("currencycode") ||
      localStorage.getItem("currencyCode") ||
      localStorage.getItem("currency") ||
      "INR"
  ).trim() || "INR";

const channelColorMap = {
  booking: "#003580",
  bookingcom: "#003580",
  expedia: "#0A438B",
  airbnb: "#FF5A5F",
  agoda: "#58627A",
  direct: "#0F766E",
  default: "#475569",
};

const normalizeChannelCode = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/\./g, "")
    .replace(/_/g, "")
    .replace(/-/g, "");

const prettyChannelName = (value) => {
  const code = normalizeChannelCode(value);
  if (code === "booking" || code === "bookingcom") return "Booking.com";
  if (code === "expedia") return "Expedia";
  if (code === "airbnb") return "Airbnb";
  if (code === "agoda") return "Agoda";
  if (code === "direct") return "Direct";
  return value || "Unknown Channel";
};

const getChannelVisual = (value) => {
  const code = normalizeChannelCode(value);
  return {
    color: channelColorMap[code] || channelColorMap.default,
    short: prettyChannelName(value).slice(0, 2).toUpperCase(),
    code,
  };
};

const normalizeDate = (value) => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m : null;
};

const formatDate = (value) => {
  const m = normalizeDate(value);
  return m ? m.format("DD MMM YYYY") : "—";
};

const formatDateTime = (value) => {
  const m = normalizeDate(value);
  return m ? m.format("DD MMM YYYY • HH:mm") : "—";
};

const parsePayloadValue = (obj, paths = []) => {
  for (const path of paths) {
    let current = obj;
    const parts = path.split(".");
    let ok = true;

    for (const part of parts) {
      if (!current || current[part] === undefined || current[part] === null) {
        ok = false;
        break;
      }
      current = current[part];
    }

    if (ok && current !== undefined && current !== null && current !== "") {
      return current;
    }
  }
  return null;
};

const getAckState = (row) => {
  if (row?.ack_failed) return "failed";
  if (row?.is_acked) return "acked";
  return "pending";
};

const stayBucket = (row) => {
  const today = moment().startOf("day");
  const checkIn = normalizeDate(row.check_in)?.startOf("day");
  if (!checkIn) return "unknown";
  if (checkIn.isBefore(today)) return "past";
  return "upcoming";
};

const normalizeChannels = (rows = []) =>
  rows.map((row, index) => {
    const name =
      row.title || row.name || row.channel_name || row.channel_code || `Channel ${index + 1}`;
    const statusText = safeLower(
      row.last_sync_status || row.last_rate_sync_status || row.sync_status
    );
    const isConnected = row.is_connected === undefined ? false : Boolean(row.is_connected);
    const isActive = row.is_active === undefined ? true : Boolean(row.is_active);
    const hasIssue = !!row.error_message || ["failed", "error", "issue"].includes(statusText);

    return {
      key: row.id || row.channel_code || index,
      id: row.id || null,
      channel_code: row.channel_code || name,
      name: prettyChannelName(name),
      is_connected: isConnected,
      is_active: isActive,
      sync_status: statusText || "unknown",
      last_synced_at: row.last_synced_at || row.last_rate_sync_at || row.updated_at || null,
      error_message: row.error_message || null,
      visual: getChannelVisual(name),
    };
  });

const normalizeBookings = (rows = []) =>
  rows.map((row, index) => {
    const payload = row.raw_payload || row.payload || {};

    const guest_name =
      row.guest_name ||
      parsePayloadValue(payload, [
        "guest_name",
        "guest.name",
        "booking.guest_name",
        "booking.guest.name",
      ]) ||
      "Guest";

    const check_in =
      row.check_in ||
      parsePayloadValue(payload, [
        "check_in",
        "booking.check_in",
        "booking.arrival_date",
        "attributes.check_in",
      ]);

    const check_out =
      row.check_out ||
      parsePayloadValue(payload, [
        "check_out",
        "booking.check_out",
        "booking.departure_date",
        "attributes.check_out",
      ]);

    const amount =
      row.amount ||
      parsePayloadValue(payload, [
        "amount",
        "booking.amount",
        "booking.total_amount",
        "attributes.amount",
      ]);

    const currency =
      row.currency ||
      parsePayloadValue(payload, [
        "currency",
        "booking.currency",
        "attributes.currency",
      ]);

    const channel_code =
      row.channel_code ||
      parsePayloadValue(payload, [
        "channel_code",
        "channel",
        "booking.channel",
        "booking.source",
      ]) ||
      "unknown";

    return {
      key: row.revision_id || row.id || `bkg-${index}`,
      revision_id: row.revision_id || row.id || null,
      booking_id: row.booking_id || row.external_booking_id || null,
      unique_id: row.unique_id || null,
      guest_name,
      channel_code,
      channel_name: prettyChannelName(channel_code),
      check_in: check_in || null,
      check_out: check_out || null,
      amount: amount || null,
      currency: currency || null,
      status: row.status || "unknown",
      inserted_at: row.inserted_at || row.created_at || row.updated_at || null,
      is_acked: !!row.is_acked,
      ack_failed: !!row.ack_failed || safeLower(row.ack_status) === "failed",
      raw_payload: payload,
    };
  });

const KpiCard = ({ title, value, subtitle, tone = "blue", tooltip, icon }) => (
  <Card
    bordered={false}
    style={{
      boxShadow: STYLES.cardShadow,
      borderRadius: STYLES.cardBorderRadius,
      height: "100%",
      background:
        tone === "red"
          ? "linear-gradient(180deg, #ffffff, #fff8f8)"
          : tone === "green"
          ? "linear-gradient(180deg, #ffffff, #f8fffb)"
          : tone === "gold"
          ? "linear-gradient(180deg, #ffffff, #fffdf7)"
          : "linear-gradient(180deg, #ffffff, #f8fbff)",
    }}
    bodyStyle={{ padding: "18px 20px" }}
  >
    <div className="cm-kpi__head">
      <div>
        <div className="cm-kpi__label">{title}</div>
      </div>
      <div className={`cm-kpi__icon cm-kpi__icon--${tone}`}>{icon}</div>
    </div>

    <div className="cm-kpi__value">{value}</div>

    <div className="cm-kpi__footer">
      <div className="cm-kpi__meta">{subtitle || "Real-time operational metric"}</div>
      {tooltip ? (
        <Tooltip title={tooltip}>
          <InfoCircleOutlined style={{ color: "#C1C7D0", fontSize: 14 }} />
        </Tooltip>
      ) : null}
    </div>
  </Card>
);

const buildDailySeries = (rows, startDate, endDate) => {
  const series = [];
  const cursor = moment(startDate).startOf("day");
  const end = moment(endDate).startOf("day");

  while (cursor.isSameOrBefore(end)) {
    const key = cursor.format("YYYY-MM-DD");
    const sameDay = rows.filter((r) => {
      const dt = normalizeDate(r.inserted_at) || normalizeDate(r.check_in);
      return dt ? dt.format("YYYY-MM-DD") === key : false;
    });

    series.push({
      date: cursor.format("DD MMM"),
      bookings: sameDay.length,
      acked: sameDay.filter((r) => getAckState(r) === "acked").length,
      pending: sameDay.filter((r) => getAckState(r) === "pending").length,
      failed: sameDay.filter((r) => getAckState(r) === "failed").length,
    });

    cursor.add(1, "day");
  }

  return series;
};

const buildChannelMix = (rows) => {
  const map = new Map();

  rows.forEach((row) => {
    const name = prettyChannelName(row.channel_name || row.channel_code || "Unknown");
    map.set(name, (map.get(name) || 0) + 1);
  });

  return Array.from(map.entries())
    .map(([name, bookings]) => ({ name, bookings }))
    .sort((a, b) => b.bookings - a.bookings)
    .slice(0, 8);
};

const ChannelManagerLanding = () => {
  const currencyCode = getLocalCurrency();

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [channels, setChannels] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [chartMode, setChartMode] = useState("intake");
  const [searchText, setSearchText] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [dateRange, setDateRange] = useState({
    startDate: moment().startOf("month"),
    endDate: moment().endOf("month"),
  });

  const inFlightRef = useRef(false);
  const mountedRef = useRef(false);

  const loadDashboard = useCallback(
    async ({ forceSync = false, silent = false } = {}) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      if (!silent) setLoading(true);

      try {
        const channelPromise = channelsApi.listCompanyChannels();
        let bookingData = [];

        if (!forceSync && typeof channelsApi.listBookingRevisions === "function") {
          try {
            const bookingResp = await channelsApi.listBookingRevisions({
              provider_code: "channex",
            });
            bookingData = bookingResp?.data?.data || bookingResp?.data?.rows || [];
          } catch {
            bookingData = [];
          }
        }

        if (!bookingData.length) {
          const bookingResp = await channelsApi.syncBookingRevisions({
            provider_code: "channex",
            ack: false,
            limit: 100,
          });
          bookingData = bookingResp?.data?.data || [];
        }

        const channelResp = await channelPromise;
        const channelRows =
          channelResp?.data?.data || channelResp?.data?.rows || channelResp?.data || [];

        setChannels(normalizeChannels(Array.isArray(channelRows) ? channelRows : []));
        setBookings(normalizeBookings(Array.isArray(bookingData) ? bookingData : []));
        setLastUpdatedAt(moment());
      } catch (e) {
        if (!silent) {
          message.error(
            e?.response?.data?.message || "Failed to load channel manager dashboard."
          );
        }
        setChannels([]);
        setBookings([]);
      } finally {
        inFlightRef.current = false;
        if (!silent) setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    mountedRef.current = true;
    loadDashboard();

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        loadDashboard({ silent: true });
      }
    }, AUTO_REFRESH_MS);

    const handleVisibility = () => {
      if (document.visibilityState === "visible" && mountedRef.current) {
        loadDashboard({ silent: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [loadDashboard]);

  const handleDateChange = (param) => {
    if (param.start && param.end) {
      setDateRange({
        startDate: moment(param.start).startOf("day"),
        endDate: moment(param.end).endOf("day"),
      });
    }
  };

  const onSyncFeed = async () => {
    setSyncing(true);
    try {
      await loadDashboard({ forceSync: true });
      message.success("Booking feed refreshed.");
    } finally {
      setSyncing(false);
    }
  };

  const filteredChannels = useMemo(() => {
    const q = safeLower(searchText);
    if (!q) return channels;

    return channels.filter(
      (c) =>
        safeLower(c.name).includes(q) ||
        safeLower(c.channel_code).includes(q) ||
        safeLower(c.sync_status).includes(q)
    );
  }, [channels, searchText]);

  const filteredBookings = useMemo(() => {
    const q = safeLower(searchText);

    return bookings.filter((row) => {
      const dt = normalizeDate(row.inserted_at) || normalizeDate(row.check_in);
      const inRange = dt
        ? dt.isBetween(
            dateRange.startDate.clone().startOf("day"),
            dateRange.endDate.clone().endOf("day"),
            undefined,
            "[]"
          )
        : true;

      const matchesSearch =
        !q ||
        safeLower(row.guest_name).includes(q) ||
        safeLower(row.booking_id).includes(q) ||
        safeLower(row.revision_id).includes(q) ||
        safeLower(row.channel_name).includes(q);

      return inRange && matchesSearch;
    });
  }, [bookings, searchText, dateRange]);

  const stats = useMemo(() => {
    const totalChannels = channels.length;
    const connectedChannels = channels.filter((c) => c.is_connected).length;
    const activeChannels = channels.filter((c) => c.is_connected && c.is_active).length;
    const issueChannels = channels.filter(
      (c) => c.error_message || c.sync_status === "failed" || !c.is_connected
    ).length;

    const pendingAck = filteredBookings.filter((b) => getAckState(b) === "pending").length;
    const failedAck = filteredBookings.filter((b) => getAckState(b) === "failed").length;
    const upcomingBookings = filteredBookings.filter(
      (b) => stayBucket(b) === "upcoming"
    ).length;

    return {
      totalChannels,
      connectedChannels,
      activeChannels,
      issueChannels,
      pendingAck,
      failedAck,
      upcomingBookings,
    };
  }, [channels, filteredBookings]);

  const dailySeries = useMemo(
    () => buildDailySeries(filteredBookings, dateRange.startDate, dateRange.endDate),
    [filteredBookings, dateRange]
  );

  const channelMix = useMemo(() => buildChannelMix(filteredBookings), [filteredBookings]);

  const channelListWithCounts = useMemo(() => {
    const totalVisible = filteredBookings.length || 1;

    return filteredChannels
      .map((channel) => {
        const bookingCount = filteredBookings.filter(
          (b) =>
            normalizeChannelCode(b.channel_name || b.channel_code) ===
            normalizeChannelCode(channel.name || channel.channel_code)
        ).length;

        return {
          ...channel,
          bookingCount,
          share: Math.round((bookingCount / totalVisible) * 100),
        };
      })
      .sort((a, b) => {
        const issueScoreA =
          a.error_message || a.sync_status === "failed" || !a.is_connected ? 1 : 0;
        const issueScoreB =
          b.error_message || b.sync_status === "failed" || !b.is_connected ? 1 : 0;
        if (issueScoreA !== issueScoreB) return issueScoreB - issueScoreA;
        return b.bookingCount - a.bookingCount;
      });
  }, [filteredChannels, filteredBookings]);

  const recentColumns = [
    {
      title: "Guest / Booking",
      key: "guest_name",
      render: (_, row) => (
        <div>
          <div className="cm-landing__guest">{row.guest_name}</div>
          <div className="cm-landing__sub">
            {row.booking_id || row.revision_id || "No booking reference"}
          </div>
        </div>
      ),
    },
    {
      title: "Channel",
      key: "channel",
      render: (_, row) => {
        const visual = getChannelVisual(row.channel_name || row.channel_code);
        return (
          <div className="d-flex align-items-center gap-2">
            <Avatar
              size={26}
              style={{
                backgroundColor: visual.color,
                color: "#fff",
                fontSize: 11,
                fontWeight: 800,
              }}
            >
              {visual.short}
            </Avatar>
            <span>{prettyChannelName(row.channel_name || row.channel_code)}</span>
          </div>
        );
      },
    },
    {
      title: "Stay",
      key: "stay",
      render: (_, row) => (
        <div>
          <div className="cm-landing__stay">
            {formatDate(row.check_in)} → {formatDate(row.check_out)}
          </div>
          <div className="cm-landing__sub">
            {row.amount ? `${row.currency || currencyCode} ${row.amount}` : `${currencyCode} —`}
          </div>
        </div>
      ),
    },
    {
      title: "Ack",
      key: "ack",
      render: (_, row) => {
        const state = getAckState(row);
        if (state === "acked") return <Tag color="green">Acknowledged</Tag>;
        if (state === "failed") return <Tag color="red">Failed</Tag>;
        return <Tag color="gold">Pending</Tag>;
      },
    },
    {
      title: "Received",
      dataIndex: "inserted_at",
      key: "inserted_at",
      render: (value) => (
        <Tooltip title={formatDateTime(value)}>
          <span style={{ color: STYLES.textLight, fontWeight: 700 }}>
            {value ? moment(value).fromNow() : "—"}
          </span>
        </Tooltip>
      ),
    },
  ];

  const downloadMenu = (
    <Menu>
      <Menu.Item
        key="csv"
        onClick={() => {
          const headers = [
            "Guest",
            "Channel",
            "Booking ID",
            "Revision ID",
            "Check In",
            "Check Out",
            "Status",
            "Ack",
            "Amount",
            "Currency",
          ];

          const rows = filteredBookings.map((r) => [
            r.guest_name || "",
            prettyChannelName(r.channel_name || r.channel_code),
            r.booking_id || "",
            r.revision_id || "",
            formatDate(r.check_in),
            formatDate(r.check_out),
            r.status || "",
            getAckState(r),
            r.amount || "",
            r.currency || currencyCode,
          ]);

          const csv = [headers, ...rows]
            .map((row) =>
              row
                .map((cell) => {
                  const s = String(cell ?? "");
                  return s.includes(",") || s.includes('"')
                    ? `"${s.replace(/"/g, '""')}"`
                    : s;
                })
                .join(",")
            )
            .join("\n");

          const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "channel-manager-activity.csv";
          a.click();
          URL.revokeObjectURL(url);
        }}
      >
        Download CSV
      </Menu.Item>
      <Menu.Item key="bookings">
        <Link to="/channel-manager/bookings">Open Bookings</Link>
      </Menu.Item>
    </Menu>
  );

  return (
    <div
      className="page-wrapper-full cm-landing"
      style={{ backgroundColor: "#F4F5F7", minHeight: "100vh" }}
    >
      <div className="content container-fluid">
        <div className="cm-landing__hero">
          <div className="cm-landing__hero-copy">
            <div className="cm-landing__eyebrow">Channel Operations</div>
            <Title level={2} className="cm-landing__title">
              Channel Manager
            </Title>
            <Text className="cm-landing__subtitle">
              Monitor channel connectivity, booking intake, acknowledgement health, and recent OTA activity from one operational workspace.
            </Text>
          </div>

          <div className="cm-landing__hero-meta">
            <div className="cm-landing__hero-meta-item">
              <span className="cm-landing__hero-meta-label">Last updated</span>
              <span className="cm-landing__hero-meta-value">
                {lastUpdatedAt ? lastUpdatedAt.fromNow() : "Not available"}
              </span>
            </div>
            <div className="cm-landing__hero-meta-item">
              <span className="cm-landing__hero-meta-label">Display currency</span>
              <span className="cm-landing__hero-meta-value">{currencyCode}</span>
            </div>
          </div>
        </div>

        <Card bordered={false} className="cm-landing__command-card">
          <div className="cm-landing__toolbar">
            <DatePicker
              initialSettings={{
                singleDatePicker: false,
                showDropdowns: true,
                startDate: dateRange.startDate.toDate(),
                endDate: dateRange.endDate.toDate(),
                ranges: {
                  Today: [moment().toDate(), moment().toDate()],
                  "This Week": [moment().startOf("week").toDate(), moment().endOf("week").toDate()],
                  "This Month": [moment().startOf("month").toDate(), moment().endOf("month").toDate()],
                  "Next Month": [
                    moment().add(1, "month").startOf("month").toDate(),
                    moment().add(1, "month").endOf("month").toDate(),
                  ],
                },
                locale: {
                  format: "DD MMM YYYY",
                  applyLabel: "Apply",
                  cancelLabel: "Cancel",
                },
              }}
              onCallback={(start, end) => handleDateChange({ start, end })}
            >
              <div className="cm-landing__date-field">
                <CalendarOutlined />
                <span>
                  {dateRange.startDate.format("DD MMM YYYY")} -{" "}
                  {dateRange.endDate.format("DD MMM YYYY")}
                </span>
                <DownOutlined />
              </div>
            </DatePicker>

            <div className="cm-landing__search-wrap">
              <Input
                placeholder="Search channel, guest, booking..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="cm-landing__search-input"
                allowClear
              />
              <Button type="primary" className="cm-landing__search-btn">
                Search
              </Button>
            </div>

            <div className="cm-landing__actions">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => loadDashboard()}
                loading={loading}
                className="cm-landing__toolbar-btn"
              >
                Refresh
              </Button>

              <Button
                icon={<SyncOutlined />}
                onClick={onSyncFeed}
                loading={syncing}
                className="cm-landing__toolbar-btn cm-landing__toolbar-btn--accent"
              >
                Sync Feed
              </Button>

              <Dropdown overlay={downloadMenu} trigger={["click"]}>
                <Button className="cm-landing__toolbar-btn">
                  <DownloadOutlined /> Export <DownOutlined />
                </Button>
              </Dropdown>

              <Link to="/channel-manager/bookings" className="btn btn-light cm-landing__cta-link">
                <LinkOutlined /> Open Bookings
              </Link>

              <Link to="/channel-manager/setup" className="btn add-btn custom-btn cm-landing__cta-link">
                <i className="la la-plus-circle custom-icon" /> Connect Channel
              </Link>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="cm-landing__loading">
            <Spin size="large" />
          </div>
        ) : (
          <>
            <Row gutter={[18, 18]} className="cm-landing__stats mb-4">
              <Col xs={24} sm={12} md={8} lg={4}>
                <KpiCard
                  title="Total Channels"
                  value={stats.totalChannels}
                  subtitle="Configured distribution channels"
                  tone="blue"
                  icon={<LinkOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <KpiCard
                  title="Connected"
                  value={stats.connectedChannels}
                  subtitle="Channels currently connected"
                  tone="green"
                  icon={<CheckCircleOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <KpiCard
                  title="Active"
                  value={stats.activeChannels}
                  subtitle="Connected and enabled"
                  tone="blue"
                  icon={<LinkOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <KpiCard
                  title="Sync Issues"
                  value={stats.issueChannels}
                  subtitle="Channels needing attention"
                  tone="red"
                  icon={<ExclamationCircleOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <KpiCard
                  title="Pending Ack"
                  value={stats.pendingAck}
                  subtitle="Booking revisions awaiting acknowledgement"
                  tone="gold"
                  icon={<ClockCircleOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <KpiCard
                  title="Failed Ack"
                  value={stats.failedAck}
                  subtitle="Revisions that need retry"
                  tone="red"
                  icon={<WarningOutlined />}
                />
              </Col>
            </Row>

            <Row gutter={[18, 18]}>
              <Col xs={24} lg={16}>
                <Card bordered={false} className="cm-landing__panel-card">
                  <div className="cm-landing__panel-head">
                    <div>
                      <div className="cm-landing__panel-title">Operations Trend</div>
                      <div className="cm-landing__panel-subtitle">
                        Booking intake, acknowledgement health, and channel concentration based on visible activity.
                      </div>
                    </div>
                  </div>

                  <Tabs
                    activeKey={chartMode}
                    onChange={setChartMode}
                    size="small"
                    className="cm-landing__tabs"
                  >
                    <TabPane tab="Booking Intake" key="intake" />
                    <TabPane tab="Ack Health" key="ack" />
                    <TabPane tab="Channel Mix" key="channel" />
                  </Tabs>

                  {chartMode === "intake" && (
                    <div style={{ height: 380 }}>
                      {dailySeries.some((x) => x.bookings > 0) ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={dailySeries}>
                            <defs>
                              <linearGradient id="intakeFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#2563EB" stopOpacity={0.22} />
                                <stop offset="95%" stopColor="#2563EB" stopOpacity={0.02} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#eef2f7" vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748B" }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748B" }} />
                            <RechartsTooltip />
                            <Area
                              type="monotone"
                              dataKey="bookings"
                              stroke="#2563EB"
                              strokeWidth={3}
                              fill="url(#intakeFill)"
                              name="Bookings"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <Empty description="No booking intake available for the selected range." />
                      )}
                    </div>
                  )}

                  {chartMode === "ack" && (
                    <div style={{ height: 380 }}>
                      {dailySeries.some((x) => x.acked > 0 || x.pending > 0 || x.failed > 0) ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={dailySeries}>
                            <CartesianGrid stroke="#eef2f7" vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748B" }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748B" }} />
                            <RechartsTooltip />
                            <Legend />
                            <Bar dataKey="acked" stackId="a" fill="#10B981" name="Acknowledged" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="pending" stackId="a" fill="#F59E0B" name="Pending" />
                            <Bar dataKey="failed" stackId="a" fill="#EF4444" name="Failed" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <Empty description="No acknowledgement data available for the selected range." />
                      )}
                    </div>
                  )}

                  {chartMode === "channel" && (
                    <div style={{ height: 380 }}>
                      {channelMix.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={channelMix} layout="vertical" margin={{ left: 24 }}>
                            <CartesianGrid stroke="#eef2f7" horizontal={false} />
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#64748B" }} />
                            <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 12, fill: "#0F172A" }} />
                            <RechartsTooltip />
                            <Bar dataKey="bookings" fill="#2563EB" radius={[0, 6, 6, 0]} name="Bookings" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <Empty description="No channel mix available for the selected range." />
                      )}
                    </div>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={8}>
                <Card
                  bordered={false}
                  className="cm-landing__panel-card"
                  bodyStyle={{ height: "100%" }}
                >
                  <div className="cm-landing__panel-head">
                    <div>
                      <div className="cm-landing__panel-title">Channel Health</div>
                      <div className="cm-landing__panel-subtitle">
                        Connectivity, sync state, and booking share by channel.
                      </div>
                    </div>
                    <Tooltip title="Live status of configured channels">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </div>

                  {channelListWithCounts.length ? (
                    <List
                      itemLayout="horizontal"
                      dataSource={channelListWithCounts}
                      renderItem={(item) => (
                        <List.Item className="cm-landing__health-row">
                          <div className="d-flex align-items-center w-100 justify-content-between">
                            <div className="d-flex align-items-center gap-3">
                              <div style={{ position: "relative" }}>
                                <Avatar
                                  size={46}
                                  style={{
                                    backgroundColor: item.visual.color,
                                    color: "#fff",
                                    fontWeight: 800,
                                    border: "2px solid #fff",
                                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                                  }}
                                >
                                  {item.visual.short}
                                </Avatar>
                                <span
                                  className="cm-landing__health-dot"
                                  style={{
                                    backgroundColor:
                                      item.error_message || item.sync_status === "failed"
                                        ? STYLES.danger
                                        : item.is_connected && item.is_active
                                        ? STYLES.success
                                        : STYLES.warning,
                                  }}
                                />
                              </div>

                              <div>
                                <h6 className="cm-landing__health-name">{item.name}</h6>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <small className="cm-landing__health-meta">
                                    Last sync:{" "}
                                    {item.last_synced_at
                                      ? moment(item.last_synced_at).fromNow()
                                      : "Not available"}
                                  </small>
                                  {item.error_message ? (
                                    <Tag color="red" style={{ margin: 0 }}>
                                      Issue
                                    </Tag>
                                  ) : !item.is_connected ? (
                                    <Tag style={{ margin: 0 }}>Disconnected</Tag>
                                  ) : item.is_active ? (
                                    <Tag color="green" style={{ margin: 0 }}>
                                      Active
                                    </Tag>
                                  ) : (
                                    <Tag color="gold" style={{ margin: 0 }}>
                                      Paused
                                    </Tag>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="cm-landing__health-right">
                              <div className="cm-landing__health-count">{item.bookingCount}</div>
                              <div className="cm-landing__health-label">bookings</div>
                              <div className="cm-landing__health-bar">
                                <div
                                  className="cm-landing__health-bar-fill"
                                  style={{
                                    width: `${item.share || 0}%`,
                                    background: item.visual.color,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </List.Item>
                      )}
                    />
                  ) : (
                    <Empty description="No channels available." image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </Card>
              </Col>
            </Row>

            <Row className="mt-4">
              <Col span={24}>
                <Card bordered={false} className="cm-landing__panel-card">
                  <div className="cm-landing__panel-head">
                    <div>
                      <div className="cm-landing__panel-title">Recent OTA Activity</div>
                      <div className="cm-landing__panel-subtitle">
                        Latest visible booking movement across connected channels.
                      </div>
                    </div>
                    <Link to="/channel-manager/bookings" className="cm-landing__view-all">
                      View All
                    </Link>
                  </div>

                  {filteredBookings.length ? (
                    <Table
                      columns={recentColumns}
                      dataSource={filteredBookings.slice(0, 10)}
                      rowKey="key"
                      pagination={false}
                      className="cm-landing__table"
                      scroll={{ x: 980 }}
                    />
                  ) : (
                    <Empty
                      description="No OTA activity available for the selected range."
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                </Card>
              </Col>
            </Row>
          </>
        )}

        <style>{`
          .cm-landing__hero {
            display: flex;
            justify-content: space-between;
            gap: 20px;
            margin-bottom: 16px;
          }

          .cm-landing__eyebrow {
            display: inline-flex;
            align-items: center;
            padding: 6px 10px;
            border-radius: 999px;
            background: #eff6ff;
            color: #1d4ed8;
            font-size: 12px;
            font-weight: 800;
            letter-spacing: .3px;
            margin-bottom: 10px;
          }

          .cm-landing__title {
            margin-bottom: 4px !important;
            font-weight: 800 !important;
            color: #0F172A !important;
          }

          .cm-landing__subtitle {
            color: rgba(15,23,42,.62);
            font-size: 14px;
            line-height: 1.6;
            max-width: 760px;
          }

          .cm-landing__hero-meta {
            display: grid;
            gap: 10px;
            min-width: 220px;
          }

          .cm-landing__hero-meta-item {
            background: #fff;
            border: 1px solid #edf2f7;
            border-radius: 14px;
            padding: 12px 14px;
            box-shadow: 0 8px 24px rgba(15,23,42,.04);
          }

          .cm-landing__hero-meta-label {
            display: block;
            color: rgba(15,23,42,.55);
            font-size: 12px;
            margin-bottom: 2px;
          }

          .cm-landing__hero-meta-value {
            display: block;
            font-weight: 800;
            color: #0F172A;
          }

          .cm-landing__command-card,
          .cm-landing__panel-card {
            border-radius: 20px !important;
            box-shadow: 0 12px 36px rgba(15,23,42,.06);
            border: none !important;
          }

          .cm-landing__command-card {
            margin-bottom: 16px;
          }

          .cm-landing__toolbar {
            display: grid;
            grid-template-columns: 250px minmax(320px, 1fr) auto;
            gap: 14px;
            align-items: center;
          }

          .cm-landing__date-field {
            height: 44px;
            padding: 0 14px;
            border: 1px solid #dbe3ee;
            border-radius: 12px;
            background: #fff;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-weight: 700;
            cursor: pointer;
            min-width: 250px;
          }

          .cm-landing__search-wrap {
            display: grid;
            grid-template-columns: 1fr 104px;
          }

          .cm-landing__search-input {
            height: 44px !important;
            border-radius: 12px 0 0 12px !important;
          }

          .cm-landing__search-btn {
            height: 44px !important;
            border-radius: 0 12px 12px 0 !important;
            font-weight: 700;
          }

          .cm-landing__actions {
            display: flex;
            align-items: center;
            gap: 10px;
            justify-content: flex-end;
            flex-wrap: wrap;
          }

          .cm-landing__toolbar-btn,
          .cm-landing__cta-link {
            height: 44px;
            border-radius: 12px !important;
            font-weight: 700;
            display: inline-flex;
            align-items: center;
            gap: 8px;
          }

          .cm-landing__toolbar-btn--accent {
            background: linear-gradient(135deg, #2563EB, #1D4ED8) !important;
            border-color: #2563EB !important;
            color: #fff !important;
          }

          .cm-landing__loading {
            min-height: 320px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .cm-landing__stats {
            margin-bottom: 16px;
          }

          .cm-kpi__head {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 12px;
          }

          .cm-kpi__label {
            color: rgba(15,23,42,.58);
            font-size: 11px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: .8px;
          }

          .cm-kpi__icon {
            width: 34px;
            height: 34px;
            border-radius: 10px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
          }

          .cm-kpi__icon--blue { background: #EAF2FF; color: #2563EB; }
          .cm-kpi__icon--green { background: #ECFDF5; color: #059669; }
          .cm-kpi__icon--red { background: #FFF1F2; color: #DC2626; }
          .cm-kpi__icon--gold { background: #FFFBEB; color: #D97706; }

          .cm-kpi__value {
            font-size: 30px;
            line-height: 1.1;
            font-weight: 800;
            color: #0F172A;
            margin-bottom: 8px;
          }

          .cm-kpi__footer {
            display: flex;
            justify-content: space-between;
            gap: 8px;
            align-items: center;
          }

          .cm-kpi__meta {
            color: #97A0AF;
            font-size: 12px;
          }

          .cm-landing__panel-head {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 12px;
            margin-bottom: 10px;
          }

          .cm-landing__panel-title {
            font-size: 16px;
            font-weight: 800;
            color: #0F172A;
            margin-bottom: 2px;
          }

          .cm-landing__panel-subtitle {
            font-size: 13px;
            color: rgba(15,23,42,.58);
          }

          .cm-landing__tabs .ant-tabs-nav {
            margin-bottom: 10px !important;
          }

          .cm-landing__tabs .ant-tabs-tab {
            font-weight: 800;
            padding-left: 0;
            padding-right: 24px;
          }

          .cm-landing__health-row {
            border-bottom: 1px solid #F4F5F7 !important;
            padding: 14px 0 !important;
          }

          .cm-landing__health-dot {
            position: absolute;
            bottom: 0;
            right: -2px;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            border: 2px solid #fff;
          }

          .cm-landing__health-name {
            margin: 0;
            font-weight: 800;
            font-size: 14px;
            color: #0F172A;
          }

          .cm-landing__health-meta {
            color: #64748B;
            font-size: 12px;
          }

          .cm-landing__health-right {
            text-align: right;
            min-width: 88px;
          }

          .cm-landing__health-count {
            font-weight: 800;
            font-size: 14px;
            color: #0F172A;
          }

          .cm-landing__health-label {
            color: #64748B;
            font-size: 11px;
          }

          .cm-landing__health-bar {
            height: 6px;
            width: 84px;
            border-radius: 999px;
            background: #ebecf0;
            overflow: hidden;
            margin-top: 6px;
            margin-left: auto;
          }

          .cm-landing__health-bar-fill {
            height: 100%;
            border-radius: 999px;
          }

          .cm-landing__table .ant-table-thead > tr > th {
            background: #f8fafc !important;
            color: #475569;
            font-weight: 800;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: .3px;
          }

          .cm-landing__table .ant-table-tbody > tr:hover > td {
            background: #fafcff !important;
          }

          .cm-landing__guest {
            font-weight: 800;
            color: #0F172A;
            margin-bottom: 2px;
          }

          .cm-landing__stay {
            font-weight: 700;
            color: #0F172A;
            margin-bottom: 2px;
          }

          .cm-landing__sub {
            font-size: 12px;
            color: rgba(15,23,42,.56);
          }

          .cm-landing__view-all {
            font-weight: 700;
          }

          @media (max-width: 1280px) {
            .cm-landing__hero {
              flex-direction: column;
            }

            .cm-landing__hero-meta {
              grid-template-columns: repeat(2, minmax(0, 1fr));
              width: 100%;
            }

            .cm-landing__toolbar {
              grid-template-columns: 1fr;
            }

            .cm-landing__actions {
              justify-content: flex-start;
            }
          }

          @media (max-width: 768px) {
            .cm-landing__hero-meta {
              grid-template-columns: 1fr;
            }

            .cm-landing__search-wrap {
              grid-template-columns: 1fr 96px;
            }

            .cm-kpi__value {
              font-size: 28px;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ChannelManagerLanding;